// Approval policy configuration page.
// Two-panel layout: category tree on left, policy detail on right.
// Parameterised by nodeType and policyType — instantiated per type in the router.
// Follows the same tree + detail pattern as AppetitePage.

import { useState, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useApi, ApiRequestError } from '@/lib/api/client'
import { useTenant } from '@/components/shared/TenantShell'
import {
  categoriesApi,
  buildCategoryTree,
  type Category,
  type CategoryTreeNode,
} from '@/lib/api/categories'
import {
  nodePoliciesApi,
  type NodeType,
  type PolicyType,
  type EffectivePolicy,
} from '@/lib/api/nodePolicies'
import { ownershipRolesApi, type OwnershipRole } from '@/lib/api/ownershipRoles'
import { queryKeys } from '@/lib/api/queryKeys'
import { ErrorState } from '@/components/shared/ErrorState'
import { TableSkeleton } from '@/components/shared/LoadingSkeleton'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { useToast } from '@/lib/hooks/use-toast'
import { cn } from '@/lib/utils'
import {
  ChevronRight,
  ChevronDown,
  Circle,
  CircleDot,
  Loader2,
  Trash2,
  Info,
} from 'lucide-react'

// --- Props ---

interface ApprovalPolicyPageProps {
  nodeType: NodeType
  policyType: PolicyType
}

// --- Helpers ---

/** Map nodeType to category API type string. */
function categoryTypeFor(nt: NodeType): 'control' | 'action' {
  return nt === 'control_category' ? 'control' : 'action'
}

// --- Page component ---

export function ApprovalPolicyPage({ nodeType, policyType }: ApprovalPolicyPageProps) {
  const { t } = useTranslation(['frameworkConfig', 'common'])
  const api = useApi()
  const { slug } = useTenant()
  const queryClient = useQueryClient()

  const catType = categoryTypeFor(nodeType)
  const catApi = categoriesApi(api, catType)
  const npApi = nodePoliciesApi(api)
  const rolesApi = ownershipRolesApi(api)

  const [selectedNodeId, setSelectedNodeId] = useState<string | null>(null)
  const [expanded, setExpanded] = useState<Set<string>>(new Set())

  // --- Queries ---

  const treeQuery = useQuery({
    queryKey: queryKeys.categories.tree(slug, catType),
    queryFn: () => catApi.tree(),
  })

  const policiesQuery = useQuery({
    queryKey: queryKeys.nodePolicies.list(slug, nodeType),
    queryFn: () => npApi.list(nodeType),
  })

  const rolesQuery = useQuery({
    queryKey: queryKeys.ownershipRoles.all(slug),
    queryFn: () => rolesApi.list(),
  })

  // Effective policy for selected node
  const effectiveQuery = useQuery({
    queryKey: queryKeys.nodePolicies.effective(slug, nodeType, selectedNodeId ?? ''),
    queryFn: () => npApi.getEffective(nodeType, selectedNodeId!),
    enabled: !!selectedNodeId,
  })

  // --- Derived data ---

  const treeNodes = useMemo(
    () => (treeQuery.data ? buildCategoryTree(treeQuery.data) : []),
    [treeQuery.data],
  )

  // Set of node IDs that have a directly-set policy (for tree indicators)
  const nodesWithPolicy = useMemo(() => {
    const set = new Set<string>()
    for (const p of policiesQuery.data?.policies ?? []) {
      set.add(p.node_id)
    }
    return set
  }, [policiesQuery.data])

  const selectedNode = treeQuery.data?.find((c) => c.id === selectedNodeId) ?? null

  // --- Loading ---

  const isLoading = treeQuery.isLoading || policiesQuery.isLoading

  if (isLoading) return <TableSkeleton />

  if (treeQuery.error) {
    return (
      <ErrorState
        message={t('common:errors.loadFailed', { resource: t(`approvals.${policyType}.title`) })}
        technicalDetail={
          treeQuery.error instanceof ApiRequestError ? treeQuery.error.message : undefined
        }
        onRetry={() => treeQuery.refetch()}
      />
    )
  }

  // --- Event handlers ---

  function toggleExpand(nodeId: string) {
    setExpanded((prev) => {
      const next = new Set(prev)
      if (next.has(nodeId)) next.delete(nodeId)
      else next.add(nodeId)
      return next
    })
  }

  function invalidatePolicies() {
    queryClient.invalidateQueries({ queryKey: queryKeys.nodePolicies.list(slug, nodeType) })
    if (selectedNodeId) {
      queryClient.invalidateQueries({
        queryKey: queryKeys.nodePolicies.effective(slug, nodeType, selectedNodeId),
      })
    }
  }

  // --- Page title ---
  const pageTitle = t(`approvals.${policyType}.title`)
  const pageDescription = t(`approvals.${policyType}.description`)

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-2xl font-bold">{pageTitle}</h1>
        <p className="text-muted-foreground">{pageDescription}</p>
      </div>

      <div className="flex h-[calc(100vh-14rem)] gap-0 overflow-hidden rounded-md border">
        {/* Left: Category tree */}
        <div className="w-2/5 overflow-y-auto border-r p-3">
          <div className="mb-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">
            {t(`approvals.${policyType}.treeLabel`)}
          </div>
          {treeNodes.map((node) => (
            <PolicyTreeRow
              key={node.id}
              node={node}
              depth={0}
              expanded={expanded}
              selectedId={selectedNodeId}
              nodesWithPolicy={nodesWithPolicy}
              onToggleExpand={toggleExpand}
              onSelect={setSelectedNodeId}
            />
          ))}
        </div>

        {/* Right: Policy detail */}
        <div className="w-3/5 overflow-y-auto p-4">
          {selectedNode ? (
            <PolicyDetailPanel
              node={selectedNode}
              nodeType={nodeType}
              policyType={policyType}
              effective={effectiveQuery.data ?? null}
              isLoading={effectiveQuery.isLoading}
              roles={rolesQuery.data ?? []}
              nodesWithPolicy={nodesWithPolicy}
              onSaved={invalidatePolicies}
            />
          ) : (
            <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
              {t('approvals.selectNode')}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

// --- Tree row component ---

interface PolicyTreeRowProps {
  node: CategoryTreeNode
  depth: number
  expanded: Set<string>
  selectedId: string | null
  nodesWithPolicy: Set<string>
  onToggleExpand: (id: string) => void
  onSelect: (id: string) => void
}

function PolicyTreeRow({
  node,
  depth,
  expanded,
  selectedId,
  nodesWithPolicy,
  onToggleExpand,
  onSelect,
}: PolicyTreeRowProps) {
  const isExpanded = expanded.has(node.id)
  const hasChildren = node.children.length > 0
  const isSelected = node.id === selectedId
  const hasPolicy = nodesWithPolicy.has(node.id)
  const indent = depth * 20

  return (
    <>
      <div
        className={cn(
          'flex cursor-pointer items-center rounded px-2 py-1.5 text-sm hover:bg-muted/50',
          isSelected && 'bg-muted',
        )}
        style={{ paddingLeft: `${indent + 8}px` }}
        onClick={() => {
          onSelect(node.id)
          if (hasChildren && !isExpanded) onToggleExpand(node.id)
        }}
      >
        {/* Expand/collapse chevron */}
        <span
          className="mr-1 w-4 flex-shrink-0"
          onClick={(e) => {
            e.stopPropagation()
            if (hasChildren) onToggleExpand(node.id)
          }}
        >
          {hasChildren ? (
            isExpanded ? (
              <ChevronDown className="h-4 w-4 text-muted-foreground" />
            ) : (
              <ChevronRight className="h-4 w-4 text-muted-foreground" />
            )
          ) : null}
        </span>

        {/* Policy indicator */}
        <span className="mr-1.5 flex-shrink-0">
          {hasPolicy ? (
            <CircleDot className="h-3.5 w-3.5 text-primary" />
          ) : (
            <Circle className="h-3.5 w-3.5 text-muted-foreground/40" />
          )}
        </span>

        {/* Node name */}
        <span className={cn('truncate', hasPolicy && 'font-semibold')}>{node.name}</span>
      </div>

      {/* Children */}
      {isExpanded &&
        node.children.map((child) => (
          <PolicyTreeRow
            key={child.id}
            node={child}
            depth={depth + 1}
            expanded={expanded}
            selectedId={selectedId}
            nodesWithPolicy={nodesWithPolicy}
            onToggleExpand={onToggleExpand}
            onSelect={onSelect}
          />
        ))}
    </>
  )
}

// --- Detail panel component ---

interface PolicyDetailPanelProps {
  node: Category
  nodeType: NodeType
  policyType: PolicyType
  effective: EffectivePolicy | null
  isLoading: boolean
  roles: OwnershipRole[]
  nodesWithPolicy: Set<string>
  onSaved: () => void
}

function PolicyDetailPanel({
  node,
  nodeType,
  policyType,
  effective,
  isLoading,
  roles,
  nodesWithPolicy,
  onSaved,
}: PolicyDetailPanelProps) {
  const { t } = useTranslation(['frameworkConfig', 'common'])
  const api = useApi()
  const { toast } = useToast()
  const npApi = nodePoliciesApi(api)

  const [selectedRoleId, setSelectedRoleId] = useState<string>('')

  // Sync role dropdown when effective policy loads or node changes
  const effectiveRoleId = effective?.config?.role_id ?? ''
  if (effective && selectedRoleId === '' && effectiveRoleId) {
    // Initial sync — don't set dirty
    setSelectedRoleId(effectiveRoleId)
  }

  // Reset when node changes
  const [lastNodeId, setLastNodeId] = useState(node.id)
  if (node.id !== lastNodeId) {
    setLastNodeId(node.id)
    setSelectedRoleId(effective?.config?.role_id ?? '')
  }

  const hasOwnPolicy = nodesWithPolicy.has(node.id)
  const isInherited = effective?.is_inherited ?? false
  const hasAnyPolicy = effective?.enabled ?? false

  // Determine if the user has changed the role from the effective state
  const isDirty = selectedRoleId !== '' && selectedRoleId !== effectiveRoleId

  // Save mutation
  const saveMutation = useMutation({
    mutationFn: () => npApi.set(nodeType, node.id, { role_id: selectedRoleId }),
    onSuccess: () => {
      onSaved()
      toast({ title: t('approvals.saved') })
    },
    onError: (err) => {
      toast({
        title: t('common:errors.generic'),
        description: err instanceof ApiRequestError ? err.message : t('common:errors.generic'),
        variant: 'destructive',
      })
    },
  })

  // Remove mutation
  const removeMutation = useMutation({
    mutationFn: () => npApi.remove(nodeType, node.id),
    onSuccess: () => {
      setSelectedRoleId('')
      onSaved()
      toast({ title: t('approvals.removed') })
    },
    onError: (err) => {
      toast({
        title: t('common:errors.generic'),
        description: err instanceof ApiRequestError ? err.message : t('common:errors.generic'),
        variant: 'destructive',
      })
    },
  })

  if (isLoading) {
    return (
      <div className="flex h-full items-center justify-center">
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
      </div>
    )
  }

  // Status line
  let statusText: string
  let statusVariant: 'default' | 'secondary' | 'outline' = 'outline'
  if (hasOwnPolicy) {
    statusText = t('approvals.policySetOnNode')
    statusVariant = 'default'
  } else if (isInherited && effective?.inherited_from_node_name) {
    statusText = t('approvals.inheritedFrom', { name: effective.inherited_from_node_name })
    statusVariant = 'secondary'
  } else {
    statusText = t('approvals.noPolicyConfigured')
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h2 className="text-lg font-semibold">{node.name}</h2>
        <div className="mt-1">
          <Badge variant={statusVariant}>{statusText}</Badge>
        </div>
      </div>

      {/* Info about inheritance */}
      {isInherited && hasAnyPolicy && (
        <div className="flex items-start gap-2 rounded-md border bg-muted/30 p-3 text-sm">
          <Info className="mt-0.5 h-4 w-4 flex-shrink-0 text-muted-foreground" />
          <span className="text-muted-foreground">
            {t('approvals.inheritedInfo', {
              role: effective?.role_name ?? '',
              parent: effective?.inherited_from_node_name ?? '',
            })}
          </span>
        </div>
      )}

      {/* Role selector */}
      <div className="space-y-2">
        <label htmlFor="policy-role" className="block text-sm font-medium">
          {t(`approvals.${policyType}.roleLabel`)}
        </label>
        <select
          id="policy-role"
          value={selectedRoleId}
          onChange={(e) => setSelectedRoleId(e.target.value)}
          className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
        >
          <option value="">{t('approvals.selectRole')}</option>
          {roles.map((role) => (
            <option key={role.id} value={role.id}>
              {role.label}
            </option>
          ))}
        </select>
        {isInherited && hasAnyPolicy && !hasOwnPolicy && (
          <p className="text-xs text-muted-foreground">
            {t('approvals.overrideHint')}
          </p>
        )}
      </div>

      {/* Actions */}
      <div className="flex items-center gap-2">
        <Button
          onClick={() => saveMutation.mutate()}
          disabled={!selectedRoleId || saveMutation.isPending || (!isDirty && hasOwnPolicy)}
        >
          {saveMutation.isPending ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              {t('common:saving')}
            </>
          ) : (
            t('common:save')
          )}
        </Button>

        {hasOwnPolicy && node.depth > 0 && (
          <Button
            variant="outline"
            onClick={() => removeMutation.mutate()}
            disabled={removeMutation.isPending}
          >
            {removeMutation.isPending ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <Trash2 className="mr-2 h-4 w-4" />
            )}
            {t('approvals.removePolicy')}
          </Button>
        )}
      </div>
    </div>
  )
}
